import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr, TypeUtils } from '@utils';
import { IVehicleQuestionCommonProps } from '@models';
import { DriverUsage } from '@services';
import { Grid, Stack } from '@mui/material';
import { FormikRadio } from '@components/common';
import { FormStepGreySection, FormStepLayout, FormStepQuestion } from '@components/portal/common';

const translationPath = (key: string) => `vehicle-list.add-edit-vehicle.question-vehicle-users.${key}`;

type FormFields = Extendable<{
	driverAssigned: string;
	driverUsage?: DriverUsage;
}>;

const getFormFieldValues = (values?: FormFields): FormFields => ({
	driverAssigned: TypeUtils.isDefined(values?.driverAssigned) ? (values?.driverAssigned ? '1' : '0') : '',
	driverUsage: values?.driverUsage || ''
});

export interface IQuestionVehicleUsersProps extends IVehicleQuestionCommonProps<FormFields> {}

export const QuestionVehicleUsers: FC<IQuestionVehicleUsersProps> = ({ values, onBack, onNext, ...props }) => {
	const { t } = useTranslation('portal');
	const [initialValues, setInitialValues] = useState<FormFields>(getFormFieldValues(values));
	useEffect(() => setInitialValues(getFormFieldValues(values)), [values]);

	const formik = useFormik<FormFields>({
		initialValues,
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			driverAssigned: yup.string().required(t('field-required')),
			driverUsage: yup
				.string()
				.when('driverAssigned', { is: '1', then: yup.string().required(t('field-required')), otherwise: yup.string().nullable() })
		}),
		onSubmit: ({ driverAssigned, driverUsage }, api) => {
			onNext?.({ driverAssigned, driverUsage: driverAssigned === '1' ? driverUsage : undefined });
			api.setSubmitting(false);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;

	return (
		<FormStepLayout
			title={<Tr.Portal path={translationPath('title')} />}
			primaryButtonProps={{ disabled, onClick: submitForm, 'data-testid': 'add-edit-vehicle-next-button' }}
			secondaryButtonProps={{ onClick: onBack, 'data-testid': 'add-edit-vehicle-back-button' }}
			{...props}
		>
			<form onSubmit={handleSubmit}>
				<Stack direction="column" sx={{ width: 1.0 }}>
					<FormStepQuestion title={<Tr.Portal path={translationPath('first-question')} />}>
						<Grid container>
							<Grid item xs={6}>
								<FormikRadio
									formikApi={formik}
									name="driverAssigned"
									value="1"
									formControlLabelProps={{ label: <Tr.Portal path="yes" /> }}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikRadio
									formikApi={formik}
									name="driverAssigned"
									value="0"
									formControlLabelProps={{ label: <Tr.Portal path="no" /> }}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					{formik.values.driverAssigned === '1' ? (
						<FormStepGreySection>
							<FormStepQuestion title={<Tr.Portal path={translationPath('second-question')} />}>
								<Grid container>
									<Grid item xs={6}>
										<FormikRadio
											formikApi={formik}
											name="driverUsage"
											value={DriverUsage.PERSONAL}
											formControlLabelProps={{ label: <Tr.Portal path={translationPath('personal-life')} /> }}
										/>
									</Grid>
									<Grid item xs={6}>
										<FormikRadio
											formikApi={formik}
											name="driverUsage"
											value={DriverUsage.WORK}
											formControlLabelProps={{ label: <Tr.Portal path={translationPath('work')} /> }}
										/>
									</Grid>
									<Grid item xs={6}>
										<FormikRadio
											formikApi={formik}
											name="driverUsage"
											value={DriverUsage.BOTH}
											formControlLabelProps={{
												label: <Tr.Portal path={translationPath('personal-life-and-work')} />
											}}
										/>
									</Grid>
								</Grid>
							</FormStepQuestion>
						</FormStepGreySection>
					) : null}
				</Stack>
			</form>
		</FormStepLayout>
	);
};
